/**
 * KitSmartSplitDialog — opens after the admin selects a single scanned
 * Kit Admissional PDF.  The backend converts each page to PNG, classifies
 * it via Gemini Vision, identifies the employee by CPF/matrícula and
 * streams progress back via polling.
 *
 * UX:
 *   Phase 1 (processing) — large progress bar + live "page X of N…" line
 *                          + already-classified pages appearing one by one
 *                          in a left scroll list.
 *   Phase 2 (ready)      — split-pane: left = list of detected pages,
 *                          right = preview image + classification fields
 *                          (kind, contract_type, employee). Admin tweaks
 *                          overrides + clicks "Salvar selecionadas".
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, AlertCircle, FileSearch, X, FileText,
  ChevronRight, Save, RefreshCw,
} from "lucide-react";

const POLL_MS = 1500;

const KIND_OPTIONS = [
  { value: "ficha_registro",    label: "Ficha de Registro" },
  { value: "vt",                label: "Termo de Vale-Transporte" },
  { value: "cracha",            label: "Declaração de Crachá" },
  { value: "epi",               label: "Ficha de EPI" },
  { value: "contract",          label: "Contrato de Trabalho" },
  { value: "integracao_matriz", label: "Integração — Matriz" },
  { value: "integracao_filial", label: "Integração — Filial" },
  { value: "lgpd",              label: "Termo LGPD" },
  { value: "ordem_servico",     label: "Ordem de Serviço SST" },
  { value: "unknown",           label: "Não identificado" },
];

function KindBadge({ kind }) {
  const map = {
    ficha_registro:    "bg-blue-100 text-blue-800 border-blue-200",
    vt:                "bg-amber-100 text-amber-800 border-amber-200",
    cracha:            "bg-purple-100 text-purple-800 border-purple-200",
    epi:               "bg-orange-100 text-orange-800 border-orange-200",
    contract:          "bg-emerald-100 text-emerald-800 border-emerald-200",
    integracao_matriz: "bg-sky-100 text-sky-800 border-sky-200",
    integracao_filial: "bg-indigo-100 text-indigo-800 border-indigo-200",
    lgpd:              "bg-slate-100 text-slate-800 border-slate-200",
    ordem_servico:     "bg-teal-100 text-teal-800 border-teal-200",
    unknown:           "bg-rose-100 text-rose-800 border-rose-200",
  };
  const label = (KIND_OPTIONS.find((o) => o.value === kind) || {}).label
                || "Não identificado";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${map[kind] || map.unknown}`}>
      {label}
    </span>
  );
}

export default function KitSmartSplitDialog({
  open, onOpenChange, adminToken, onDone,
}) {
  const [file, setFile] = useState(null);
  const [stagingId, setStagingId] = useState("");
  const [status, setStatus] = useState(null);
  const [overrides, setOverrides] = useState({});      // {page_index: {...}}
  const [selected, setSelected] = useState({});         // {page_index: true}
  const [activePage, setActivePage] = useState(null);   // page_index
  const [previewMode, setPreviewMode] = useState("modern");  // "modern" | "scan"
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  // Reset everything when the dialog closes
  useEffect(() => {
    if (!open) {
      setFile(null); setStagingId(""); setStatus(null);
      setOverrides({}); setSelected({}); setActivePage(null);
      setPreviewMode("modern");
      setBusy(false); setSaving(false); setError("");
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);

  // Polling loop
  useEffect(() => {
    if (!stagingId) return;
    let fails = 0;
    const tick = async () => {
      try {
        const r = await axios.get(
          `${API}/admin/kit-documents/smart-split/${stagingId}/status`,
          { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 8000 },
        );
        fails = 0;
        setError("");
        setStatus(r.data);
        if (r.data?.status === "ready" || r.data?.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (r.data?.status === "ready") {
            // Pre-select pages with kind != unknown AND employee resolved.
            const preSel = {};
            const firstActive = (r.data.pages || []).find(
              (p) => p.doc_kind !== "unknown" && p.employee,
            );
            (r.data.pages || []).forEach((p) => {
              if (p.doc_kind !== "unknown" && p.employee) {
                preSel[p.page_index] = true;
              }
            });
            setSelected(preSel);
            if (firstActive && activePage == null) {
              setActivePage(firstActive.page_index);
            } else if (activePage == null && (r.data.pages || []).length) {
              setActivePage(r.data.pages[0].page_index);
            }
          }
        }
      } catch (e) {
        fails += 1;
        if (e?.response?.status === 404) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError(
            "A sessão de análise expirou ou o servidor foi reiniciado. " +
            "Feche esta janela e envie o PDF novamente.",
          );
        } else if (fails >= 3) {
          setError(
            "Instabilidade na conexão com o servidor — tentando novamente " +
            "automaticamente…",
          );
        }
      }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagingId]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Envie apenas PDF");
      return;
    }
    setFile(f);
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(
        `${API}/admin/kit-documents/smart-split/upload`, fd,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        },
      );
      if (r.data?.staging_id) {
        setStagingId(r.data.staging_id);
        setStatus({
          status: "processing", total_pages: r.data.total_pages || 0,
          processed_pages: 0, current_step: "Iniciando…", pages: [],
        });
      } else {
        toast.error("Resposta inesperada");
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Falha no upload");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const pages = useMemo(() => status?.pages || [], [status]);
  const total = status?.total_pages || 0;
  const done = status?.processed_pages || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isProcessing = status?.status === "processing";
  const isReady = status?.status === "ready";
  const isError = status?.status === "error";
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const activePageData = pages.find((p) => p.page_index === activePage);

  // Effective kind / employee / contract_type for the active page (apply overrides)
  const effective = (p) => {
    if (!p) return { kind: "unknown", employeeId: "", contractType: null,
                     employee: null };
    const ov = overrides[p.page_index] || {};
    return {
      kind: ov.doc_kind || p.doc_kind || "unknown",
      employeeId: ov.employee_id || p.employee?.id || "",
      contractType: ov.contract_type ?? p.contract_type ?? null,
      employee: ov._employee_override || p.employee || null,
    };
  };

  // Group pages by employee.  Resolution order (per page):
  //   1. Resolved employee.id (DB match) — most stable.
  //   2. Detected nome (normalized) — same person across pages even if CPF
  //      OCR was inconsistent.
  //   3. Detected CPF.
  //   4. "unidentified".
  const groups = useMemo(() => {
    const map = new Map();
    const normNome = (s) => (s || "").trim().toUpperCase().replace(/\s+/g, " ");
    pages.forEach((p) => {
      const ov = overrides[p.page_index] || {};
      const emp = ov._employee_override || p.employee;
      let key, label, sub;
      if (emp?.id) {
        key = `emp:${emp.id}`;
        label = emp.nome || "?";
        sub = `Matr ${emp.matricula || "—"}`;
      } else if (normNome(p.detected_nome)) {
        key = `nome:${normNome(p.detected_nome)}`;
        label = p.detected_nome;
        sub = p.detected_cpf
          ? `CPF ${p.detected_cpf} · Não cadastrado`
          : "Não cadastrado";
      } else if (p.detected_cpf) {
        key = `cpf:${p.detected_cpf}`;
        label = `CPF ${p.detected_cpf}`;
        sub = "Não cadastrado";
      } else {
        key = "unidentified";
        label = "Não identificado";
        sub = "—";
      }
      if (!map.has(key)) map.set(key, { key, label, sub, pages: [] });
      map.get(key).pages.push(p);
    });
    return Array.from(map.values());
  }, [pages, overrides]);

  const updateOverride = (pidx, patch) => {
    setOverrides((cur) => ({
      ...cur,
      [pidx]: { ...(cur[pidx] || {}), ...patch },
    }));
  };

  const onConfirm = async () => {
    const selections = pages
      .filter((p) => selected[p.page_index])
      .map((p) => {
        const ov = overrides[p.page_index] || {};
        return {
          page_index: p.page_index,
          doc_kind: ov.doc_kind || p.doc_kind,
          contract_type: ov.contract_type ?? p.contract_type,
          employee_id: ov.employee_id || p.employee?.id,
        };
      });
    if (!selections.length) {
      toast.error("Selecione ao menos uma página");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/kit-documents/smart-split/${stagingId}/confirm`,
        { selections },
        { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 60000 },
      );
      const ok = r.data?.ok_count || 0;
      const fail = r.data?.failed_count || 0;
      if (ok && !fail) toast.success(`${ok} documento(s) criado(s)!`);
      else if (ok && fail) toast.warning(`${ok} OK, ${fail} falharam`);
      else toast.error("Nenhum documento foi criado");
      if (onDone) onDone(r.data);
      onOpenChange(false);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Falha ao confirmar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (!v && (isProcessing || saving)) return;
      onOpenChange(v);
    }}>
      <DialogContent
        className={isReady ? "max-w-6xl w-[95vw] h-[92vh] flex flex-col" : "max-w-2xl"}
        data-testid="kit-smart-split-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSearch className="w-5 h-5 text-sky-600" />
            Smart Splitter — Kit Admissional escaneado
          </DialogTitle>
          <DialogDescription className="sr-only">
            Envie um PDF único contendo várias páginas de Kit Admissional;
            o sistema separa, classifica cada documento via IA e identifica
            o funcionário automaticamente.
          </DialogDescription>
        </DialogHeader>

        {/* PHASE 0 — pick a PDF */}
        {!stagingId && (
          <div className="space-y-4 py-2">
            <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800">
              Envie um único PDF contendo várias páginas escaneadas (uma por
              documento). O sistema vai separar, classificar e identificar
              automaticamente o funcionário em cada página. Você revisa e
              confirma na próxima tela.
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              onChange={onPick}
              className="hidden"
              data-testid="smart-split-input"
            />
            <div className="flex justify-center py-6">
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                size="lg"
                data-testid="smart-split-pick-btn"
              >
                {busy
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando…</>
                  : <>Selecionar PDF do Kit completo</>}
              </Button>
            </div>
          </div>
        )}

        {/* PHASE 1 — processing */}
        {stagingId && !isReady && (
          <div className="space-y-4 py-2">
            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
                {error}
              </div>
            )}
            {isError && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                {status?.error || "Falha ao processar o PDF"}
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>
                  {isError ? "Erro" : "Analisando"} — {done} de {total || "?"} página(s)
                </span>
                <span className="font-semibold text-slate-900">{pct}%</span>
              </div>
              <Progress value={pct} className="h-2" />
            </div>

            {!isError && (
              <div className="rounded-md border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-800 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {status?.current_step || "Aguarde…"}
              </div>
            )}

            {/* Live list as pages are classified */}
            <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
              {pages.length === 0 && (
                <div className="text-center text-xs text-slate-500 py-6">
                  Nenhuma página processada ainda…
                </div>
              )}
              {pages.map((p) => (
                <div
                  key={p.page_index}
                  className="px-3 py-2 flex items-center gap-3 text-xs"
                  data-testid={`smart-split-live-page-${p.page_index}`}
                >
                  <span className="font-mono text-slate-400">
                    P{p.page_index + 1}
                  </span>
                  <KindBadge kind={p.doc_kind} />
                  {p.contract_type && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-mono">
                      {p.contract_type}
                    </span>
                  )}
                  <span className="flex-1 truncate text-slate-700">
                    {p.employee
                      ? `${p.employee.matricula || "—"} · ${p.employee.nome || "?"}`
                      : (p.detected_nome || "Funcionário não localizado")}
                  </span>
                  {p.employee
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    : <AlertCircle  className="w-3.5 h-3.5 text-amber-600" />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PHASE 2 — ready */}
        {isReady && (
          <div className="flex-1 min-h-0 grid grid-cols-12 gap-3 py-2">
            {/* LEFT — pages list grouped by employee */}
            <div className="col-span-5 flex flex-col min-h-0 border rounded-md border-slate-200">
              <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 flex items-center justify-between">
                <span>Páginas detectadas ({pages.length}) · {groups.length} func.</span>
                <span className="text-[11px] text-slate-500">
                  {selectedCount} selecionada(s)
                </span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {groups.map((g) => {
                  const allChecked = g.pages.every((p) => selected[p.page_index]);
                  const anyChecked = g.pages.some((p) => selected[p.page_index]);
                  return (
                    <div key={g.key} data-testid={`smart-split-group-${g.key}`}>
                      <div className="sticky top-0 bg-slate-100 border-y border-slate-200 px-3 py-2 flex items-center gap-2 z-10">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={(el) => {
                            if (el) el.indeterminate = !allChecked && anyChecked;
                          }}
                          onChange={(e) => {
                            setSelected((s) => {
                              const next = { ...s };
                              g.pages.forEach((p) => {
                                next[p.page_index] = e.target.checked;
                              });
                              return next;
                            });
                          }}
                          data-testid={`smart-split-group-check-${g.key}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-semibold text-slate-800 truncate">
                            {g.label}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            {g.sub} · {g.pages.length} pág(s)
                          </div>
                        </div>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {g.pages.map((p) => {
                          const isActive = activePage === p.page_index;
                          const isSel = !!selected[p.page_index];
                          return (
                            <div
                              key={p.page_index}
                              onClick={() => setActivePage(p.page_index)}
                              className={`px-3 py-2 cursor-pointer flex items-start gap-2 text-xs ${
                                isActive ? "bg-sky-50" : "hover:bg-slate-50"
                              }`}
                              data-testid={`smart-split-row-${p.page_index}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSel}
                                onChange={(e) => {
                                  setSelected((s) => ({
                                    ...s, [p.page_index]: e.target.checked,
                                  }));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="mt-1"
                                data-testid={`smart-split-check-${p.page_index}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="font-mono text-slate-400">
                                    P{p.page_index + 1}
                                  </span>
                                  <KindBadge
                                    kind={(overrides[p.page_index]?.doc_kind)
                                          || p.doc_kind}
                                  />
                                  {((overrides[p.page_index]?.contract_type)
                                    ?? p.contract_type) && (
                                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-mono">
                                      {(overrides[p.page_index]?.contract_type)
                                        ?? p.contract_type}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[10px] text-slate-400 truncate">
                                  {(p.confidence * 100 | 0)}% · {p.summary}
                                </div>
                              </div>
                              {isActive && <ChevronRight className="w-3.5 h-3.5 text-sky-600 mt-1" />}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT — preview + override controls */}
            <div className="col-span-7 flex flex-col min-h-0 border rounded-md border-slate-200">
              {!activePageData ? (
                <div className="flex-1 flex items-center justify-center text-xs text-slate-400">
                  Selecione uma página à esquerda…
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 flex items-center justify-between">
                    <span>Página {activePageData.page_index + 1} — Preview do modelo AVI</span>
                    <PreviewToggle
                      value={previewMode}
                      onChange={setPreviewMode}
                    />
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    {previewMode === "modern" ? (
                      <PreviewModernPdf
                        url={`${API}/admin/kit-documents/smart-split/${stagingId}/page/${activePageData.page_index}/preview-modern`}
                        params={{
                          kind: effective(activePageData).kind,
                          employee_id: effective(activePageData).employeeId,
                          contract_type: effective(activePageData).contractType || "",
                        }}
                        adminToken={adminToken}
                        fallbackUrl={`${API}/admin/kit-documents/smart-split/${stagingId}/page/${activePageData.page_index}/image`}
                      />
                    ) : (
                      <PreviewImageWithAuth
                        url={`${API}/admin/kit-documents/smart-split/${stagingId}/page/${activePageData.page_index}/image`}
                        adminToken={adminToken}
                      />
                    )}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <label className="text-[11px] text-slate-500 mb-1 block">
                          Tipo do documento
                        </label>
                        <select
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs"
                          value={(overrides[activePageData.page_index]?.doc_kind)
                                 || activePageData.doc_kind}
                          onChange={(e) => updateOverride(activePageData.page_index,
                            { doc_kind: e.target.value })}
                          data-testid="smart-split-kind-select"
                        >
                          {KIND_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      {(((overrides[activePageData.page_index]?.doc_kind)
                          || activePageData.doc_kind) === "contract") && (
                        <div>
                          <label className="text-[11px] text-slate-500 mb-1 block">
                            Subtipo do contrato
                          </label>
                          <select
                            className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs"
                            value={(overrides[activePageData.page_index]?.contract_type)
                                   ?? activePageData.contract_type ?? "indeterminado"}
                            onChange={(e) => updateOverride(activePageData.page_index,
                              { contract_type: e.target.value })}
                            data-testid="smart-split-contract-type-select"
                          >
                            <option value="indeterminado">Prazo Indeterminado</option>
                            <option value="experiencia">Experiência</option>
                            <option value="determinado">Prazo Determinado</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs space-y-1">
                      <div className="font-semibold text-slate-700 mb-1">
                        Funcionário identificado
                      </div>
                      {(() => {
                        const ovEmp = overrides[activePageData.page_index]?._employee_override;
                        const emp = ovEmp || activePageData.employee;
                        if (emp) {
                          return (
                            <>
                              <div><span className="text-slate-500">Nome:</span> {emp.nome}</div>
                              <div><span className="text-slate-500">Matrícula:</span> <span className="font-mono">{emp.matricula || "—"}</span></div>
                              <div><span className="text-slate-500">CPF:</span> <span className="font-mono">{emp.cpf || "—"}</span></div>
                              {emp.cargo && (
                                <div><span className="text-slate-500">Cargo:</span> {emp.cargo}</div>
                              )}
                              {ovEmp && (
                                <div className="text-[10px] text-amber-700 mt-1">(funcionário sobrescrito manualmente)</div>
                              )}
                            </>
                          );
                        }
                        return (
                          <>
                            <div className="text-rose-700">
                              Nenhum funcionário cadastrado bate com a página.
                            </div>
                            <div className="text-slate-500">
                              Detectado: {activePageData.detected_nome || "?"} ·
                              CPF: {activePageData.detected_cpf || "?"} ·
                              Mat: {activePageData.detected_matricula || "?"}
                            </div>
                          </>
                        );
                      })()}
                      <EmployeePicker
                        adminToken={adminToken}
                        onPick={(emp) => updateOverride(activePageData.page_index, {
                          employee_id: emp.id, _employee_override: emp,
                        })}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="border-t border-slate-100 pt-3">
          {!isReady && stagingId && (
            <Button variant="outline" disabled={isProcessing}
                    onClick={() => onOpenChange(false)}
                    data-testid="smart-split-cancel-btn">
              <X className="w-3.5 h-3.5 mr-1.5" />
              {isError ? "Fechar" : "Aguarde…"}
            </Button>
          )}
          {!stagingId && (
            <Button variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="smart-split-close-btn">
              Cancelar
            </Button>
          )}
          {isReady && (
            <div className="flex items-center gap-2 w-full justify-end">
              <Button variant="outline" disabled={saving}
                      onClick={() => onOpenChange(false)}
                      data-testid="smart-split-close-final">
                Cancelar
              </Button>
              <Button onClick={onConfirm}
                      disabled={saving || selectedCount === 0}
                      data-testid="smart-split-confirm-btn">
                {saving
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Salvando…</>
                  : <><Save className="w-3.5 h-3.5 mr-1.5" />
                       Salvar {selectedCount} pendente(s)</>}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * <img> tags can't send Authorization headers — fetch the PNG via axios
 * and render as an object URL. Replaces the in-line placeholder above.
 */
function PreviewImageWithAuth({ url, adminToken }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    let revokeRef = null;
    setBlobUrl(""); setErr("");
    (async () => {
      try {
        const r = await axios.get(url, {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob",
          timeout: 15000,
        });
        const b = URL.createObjectURL(r.data);
        revokeRef = b;
        setBlobUrl(b);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Falha ao carregar imagem");
      }
    })();
    return () => { if (revokeRef) URL.revokeObjectURL(revokeRef); };
  }, [url, adminToken]);
  if (err) return (
    <div className="text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">
      {err}
    </div>
  );
  if (!blobUrl) return (
    <div className="text-[11px] text-slate-400 italic flex items-center gap-1">
      <Loader2 className="w-3 h-3 animate-spin" /> carregando preview…
    </div>
  );
  return (
    <img src={blobUrl} alt="preview"
         className="w-full border rounded shadow-sm bg-white"
         data-testid="smart-split-preview-blob" />
  );
}

/**
 * Small segmented toggle to flip between MODERN (AVI new model) and SCAN
 * (the original scanned page) in the preview pane.
 */
function PreviewToggle({ value, onChange }) {
  const opts = [
    { v: "modern", label: "Modelo AVI" },
    { v: "scan",   label: "Escaneado" },
  ];
  return (
    <div className="inline-flex border border-slate-200 rounded-md overflow-hidden text-[11px]"
         data-testid="smart-split-preview-toggle">
      {opts.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`px-2.5 py-1 ${
            value === o.v
              ? "bg-sky-600 text-white"
              : "bg-white text-slate-600 hover:bg-slate-50"
          }`}
          data-testid={`smart-split-preview-toggle-${o.v}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Render the freshly-generated MODERN AVI PDF inside an iframe.  If the
 * backend returns 4xx (missing employee or unknown kind), fall back to
 * the scanned PNG so the admin still sees something.
 */
function PreviewModernPdf({ url, params, adminToken, fallbackUrl }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const paramKey = JSON.stringify(params || {});
  useEffect(() => {
    let revokeRef = null;
    setLoading(true); setBlobUrl(""); setErr("");
    // Guard: skip the request if mandatory params are missing — render
    // the fallback hint immediately.
    if (!params?.kind || params.kind === "unknown" || !params?.employee_id) {
      setErr("Selecione um TIPO e um FUNCIONÁRIO para gerar o preview do modelo AVI.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(url, {
          params,
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob",
          timeout: 30000,
        });
        const b = URL.createObjectURL(r.data);
        revokeRef = b;
        setBlobUrl(b);
      } catch (e) {
        setErr(e?.response?.data?.detail
               || e?.message
               || "Falha ao gerar preview moderno");
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (revokeRef) URL.revokeObjectURL(revokeRef); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, paramKey, adminToken]);

  if (loading) return (
    <div className="text-[11px] text-slate-400 italic flex items-center gap-1 py-4">
      <Loader2 className="w-3 h-3 animate-spin" /> gerando preview no modelo AVI…
    </div>
  );
  if (err) return (
    <div className="space-y-2">
      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
        {err} Mostrando o documento escaneado original abaixo.
      </div>
      <PreviewImageWithAuth url={fallbackUrl} adminToken={adminToken} />
    </div>
  );
  return (
    <iframe
      src={blobUrl}
      title="preview moderno"
      className="w-full border rounded shadow-sm bg-white"
      style={{ minHeight: 520, height: "60vh" }}
      data-testid="smart-split-preview-modern-iframe"
    />
  );
}

/**
 * Lightweight employee search — debounced, returns top 30 matches.
 */
function EmployeePicker({ adminToken, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/employees-for-list`,
          {
            params: { search: q },
            headers: { Authorization: `Bearer ${adminToken}` },
            timeout: 8000,
          },
        );
        setResults((r.data || []).slice(0, 30));
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, adminToken]);
  return (
    <div className="relative mt-2">
      <input
        type="text"
        placeholder="Buscar funcionário por nome ou matrícula…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setOpen(true)}
        className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs"
        data-testid="smart-split-emp-search"
      />
      {open && (results.length > 0 || loading) && (
        <div className="absolute z-10 mt-1 left-0 right-0 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg">
          {loading && (
            <div className="px-2 py-1 text-[11px] text-slate-400">buscando…</div>
          )}
          {results.map((e) => (
            <button
              type="button"
              key={e.id}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-sky-50 border-b border-slate-50"
              onClick={() => {
                onPick(e);
                setQ(""); setResults([]); setOpen(false);
              }}
              data-testid={`smart-split-emp-pick-${e.id}`}
            >
              <span className="font-mono text-slate-500 mr-2">
                {e.matricula || "—"}
              </span>
              {e.nome}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
